import {
  IElementCategory,
  ElementCategoryID,
} from '../../../models/element-categories.model';
import { NODON_AIR_HANDLING_UNIT_ID } from '../../../constants';
import { ElementKind, IElement } from '../../../models/project.model';
import {
  calculateVentilationFanEnergyConsumption,
  getDefaultVentilationOperationDays,
  getDefaultVentilationOperationHours,
} from '../../../helpers/energy/ventilation-energy.helpers';
import { moveLifecycleAResults } from '../../../helpers/conversion-factors.helpers';
import { Lifecycle } from '../../../models/lifecycles.model';
import { ahuProperties } from './air-handling-unit-properties';
import {
  AHU_ENERGY_UNIT,
  AHU_MASS_PER_AIRFLOW,
  AHU_VARIABLE_NAME,
  AirHandlingUnitPropertyName,
  LOCATION_COOLING_HOURS_VARIABLE,
  ACTIVITY_VENTILATION_HOURS_VARIABLE,
  POST_HEATING_ENERGY_SOURCES,
} from './air-handling-unit.model';
import { getCoolingHours } from '../../../helpers/energy/city-climate.helpers';
import { getElementPropertyResolvedCountByNameOrId } from '@shared/helpers/element-property.helpers';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { ENERGY_VARIABLE_NAME } from '@shared/helpers/energy/energy-expression-variables.models';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { requiredType } from '@shared/helpers/function.helpers';
import { replaceProperties } from '@shared/helpers/object.helpers';

const ELECTRICITY_PRODUCT_ID = 'boverket_sv-SE_6000000008';

/** Used by compute pipeline to resolve AHU vars (e.g. FAN_ENERGY_CONSUMPTION) after post_heating_demand is available. */
export const getAhuVariables = (
  variables: ExpressionVariables,
  element: IElement,
): Record<AHU_VARIABLE_NAME, number> => {
  const getProperty = (key: string): number =>
    requiredType(
      variables[key] ?? // If variable exist this is the latest value
        getElementPropertyResolvedCountByNameOrId(element, key, 0), // When running this method BEFORE the variables are resolved, this value might be obsolete (but still better than nothing)
      'number',
    );

  const postHeatingDemand =
    variables[ENERGY_VARIABLE_NAME.POST_HEATING_DEMAND] ?? 0;

  // if (typeof postHeatingDemand === 'number') {
  const ahuCount =
    variables[EXPRESSION_VARIABLE_NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY][
      ElementCategoryID.AirHandlingUnit
    ] || 1;

  const fanEnergy = calculateVentilationFanEnergyConsumption(
    getProperty(AirHandlingUnitPropertyName.Airflow),
    getProperty(AirHandlingUnitPropertyName.SpecificFanPower),
    getProperty(AirHandlingUnitPropertyName.OperationHours),
  );

  const postHeatingEnergy = postHeatingDemand / ahuCount;

  return {
    [AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION]: fanEnergy,
    [AHU_VARIABLE_NAME.POST_HEATING_ENERGY_CONSUMPTION]: postHeatingEnergy,
    [AHU_VARIABLE_NAME.TOTAL_ENERGY_CONSUMPTION]: fanEnergy + postHeatingEnergy,
  };
};

const mapResults: IElementCategory['mapResults'] = (results, context) => {
  // Only move the energy results to B6 (keep the construction cost of the Air handling unit)
  if (context.productElement.unit === AHU_ENERGY_UNIT) {
    // Move all emissions/costs from A to B6 (Operational energy use)
    return moveLifecycleAResults(results, Lifecycle.B6);
  }

  return results;
};

/***
 * Add some expression variables before the calculating the other variables.
 */
const mapExpressionVariablesBefore: IElementCategory['mapExpressionVariablesBefore'] =
  (variables, element) => {
    const coolingHours = getCoolingHours(variables.building_position);
    const activityHours = getDefaultVentilationOperationHours(
      variables.activity,
    );
    const activityDays = getDefaultVentilationOperationDays(variables.activity);

    // TOD
    return {
      ...variables,
      ...getAhuVariables(variables, element),
      [ACTIVITY_VENTILATION_HOURS_VARIABLE]: activityHours,
      [LOCATION_COOLING_HOURS_VARIABLE]: (coolingHours * activityDays) / 365,
    };
  };

const mapExpressionVariablesAfter: IElementCategory['mapExpressionVariablesAfter'] =
  (variables, element) => {
    // Only trigger changes if the variables have changed
    return replaceProperties(variables, {
      ...getAhuVariables(variables, element),
    });
  };

export const airHandlingUnit: IElementCategory = {
  id: ElementCategoryID.AirHandlingUnit,
  name: 'Air handling unit',
  color: 'rgba(255, 165, 0, 1)',
  defaultSelectedQuantity: 'energy',
  disableQuantitySelection: true,
  properties: ahuProperties,
  mapResults,
  mapExpressionVariablesBefore,
  mapExpressionVariablesAfter,
  getQuantityProperties: () => ({
    energy: {
      displayName: 'Annual energy consumption',
      displayUnit: 'kWh/yr',
      fallbackCount: `${AHU_VARIABLE_NAME.TOTAL_ENERGY_CONSUMPTION}`,
    },
    mass: {
      fallbackCount: `${AirHandlingUnitPropertyName.Airflow} / ${AHU_MASS_PER_AIRFLOW}`,
    },
  }),
  getChildElements: (element) => {
    const postHeatingProductId = getElementPropertyResolvedCountByNameOrId(
      element,
      AirHandlingUnitPropertyName.PostHeatingEnergySource,
      POST_HEATING_ENERGY_SOURCES.Electricity,
    );

    return [
      {
        kind: ElementKind.Product,
        product_id: NODON_AIR_HANDLING_UNIT_ID,
        count: 'mass',
        unit: 'kg',
      },
      {
        kind: ElementKind.Product,
        product_id: ELECTRICITY_PRODUCT_ID,
        count: `${AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION} * ${EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME}`,
        unit: AHU_ENERGY_UNIT,
      },

      // Post heating energy use
      {
        conditions: {
          [AirHandlingUnitPropertyName.SupplyAirPostHeating]: true,
        },
        nodes: [
          {
            name: 'Post heating energy',
            kind: ElementKind.Product,
            product_id: postHeatingProductId,
            count: `${AHU_VARIABLE_NAME.POST_HEATING_ENERGY_CONSUMPTION} * ${EXPRESSION_VARIABLE_NAME.BUILDING_LIFETIME}`,
            unit: AHU_ENERGY_UNIT,
          },
        ],
      },
    ];
  },
  getElementPropertySelectionByProductId: () => undefined,
};
